import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RingIcon } from '../../components/RingIcon';
import {
  CYCLE_TEMPLATES,
  MIN_RING_SIZE,
  MAX_RING_SIZE,
  type CycleTemplateId
} from '@peruselab/structure-editor/domain/ringTemplates';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

interface CyclesGroupProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  // #413 -- the rail disclosures are a single-open accordion owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
}

const PRESET_RING_SIZES = [3, 4, 5, 6, 7, 8] as const;
const DEFAULT_CUSTOM_RING_SIZE = 9;

const UNSATURATED: readonly { id: CycleTemplateId; labelKey: string }[] = [
  { id: 'cyclopentene', labelKey: 'workspace.rail.cycles.cyclopentene' },
  { id: 'cyclopentadiene', labelKey: 'workspace.rail.cycles.cyclopentadiene' },
  { id: 'cyclohexene', labelKey: 'workspace.rail.cycles.cyclohexene' },
  { id: 'cyclohexadiene', labelKey: 'workspace.rail.cycles.cyclohexadiene' },
  { id: 'benzene-kekule', labelKey: 'workspace.rail.cycles.benzeneKekule' }
];

// #252 — the left-rail Cycles disclosure, below the Bonds group. Saturated presets + custom
// size drive `ring-${n}`, aromatic benzene `benzene`, and the cycloalkene / cyclodiene
// templates `cycle-${id}` (see CYCLE_TEMPLATES). Selecting one arms the ring-placement tool the
// same way the old inline ring buttons did.
export function CyclesGroup({ tool, setTool, open, onToggle }: CyclesGroupProps) {
  const { t } = useTranslation();

  // Local input state, not derived from `tool`, so a partially-typed out-of-range value isn't
  // clamped mid-keystroke -- the tool activates only once the typed value is valid.
  const [customRingSizeInput, setCustomRingSizeInput] = useState(String(DEFAULT_CUSTOM_RING_SIZE));
  const isCustomRingActive =
    tool === `ring-${customRingSizeInput}` ||
    (tool.startsWith('ring-') && !(PRESET_RING_SIZES as readonly number[]).includes(Number(tool.slice(5))));

  const activateCustomRing = (raw: string) => {
    const value = Number(raw);
    if (Number.isInteger(value) && value >= MIN_RING_SIZE && value <= MAX_RING_SIZE) {
      setTool(`ring-${value}`);
    }
  };

  const handleCustomRingSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value;
    setCustomRingSizeInput(raw);
    activateCustomRing(raw);
  };

  return (
    <details id="rail-group-cycles" className="workspace-rail-cycles" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.cycles.groupLabel')}
      </summary>
      <div className="workspace-rail-cycles-body" role="group" aria-label={t('workspace.rail.cycles.controlsLabel')}>
        <div className="workspace-rail-cycles-grid">
          {PRESET_RING_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={`ring-tool-button ${tool === `ring-${size}` ? 'active' : ''}`}
              aria-label={t('drawingToolNav.ringLabel', { size })}
              title={t('drawingToolNav.ringTitle', { size })}
              aria-pressed={tool === `ring-${size}`}
              onClick={() => setTool(`ring-${size}`)}
            >
              <RingIcon size={size} />
            </button>
          ))}
          {UNSATURATED.map(({ id, labelKey }) => {
            const template = CYCLE_TEMPLATES[id];
            return (
              <button
                key={id}
                type="button"
                className={`ring-tool-button ${tool === `cycle-${id}` ? 'active' : ''}`}
                aria-label={t(labelKey)}
                title={t(labelKey)}
                aria-pressed={tool === `cycle-${id}`}
                onClick={() => setTool(`cycle-${id}`)}
              >
                <RingIcon size={template.size} doubleBondEdges={template.doubleBondEdges ?? []} />
              </button>
            );
          })}
          <button
            type="button"
            className={`ring-tool-button ${tool === 'benzene' ? 'active' : ''}`}
            aria-label={t('drawingToolNav.benzene')}
            title={t('drawingToolNav.benzeneTitle')}
            aria-pressed={tool === 'benzene'}
            onClick={() => setTool('benzene')}
          >
            <RingIcon size={6} aromatic />
          </button>
        </div>
        <label
          className={`custom-ring-editor ${isCustomRingActive ? 'active' : ''}`}
          title={t('drawingToolNav.customRingTitle')}
        >
          <span>{t('drawingToolNav.ringSize')}</span>
          <input
            type="number"
            min={MIN_RING_SIZE}
            max={MAX_RING_SIZE}
            step={1}
            value={customRingSizeInput}
            onChange={handleCustomRingSizeChange}
            onFocus={() => activateCustomRing(customRingSizeInput)}
          />
        </label>
      </div>
    </details>
  );
}
